// Service API pour l'Espace Pro (Vendeur/Artisan)
// Connexion aux APIs backend

import { API_URL } from '../config/api';

// Helper pour récupérer le token
const getToken = () => {
  return localStorage.getItem('buyer_token') || localStorage.getItem('ecosystem_token');
};

// Helper pour les headers
const getAuthHeaders = () => {
  const token = getToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
};

// Wrapper fetch avec gestion d'erreurs
const apiFetch = async (endpoint, options = {}) => {
  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers
    }
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Erreur réseau' }));
    throw new Error(error.detail || `Erreur ${response.status}`);
  }

  return response.json();
};

// ============ PROFIL VENDEUR ============

export const sellerProfileAPI = {
  get: () => apiFetch('/api/compte/profile'),
  
  create: (data) => apiFetch('/api/compte/profile', {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  
  update: (data) => apiFetch('/api/compte/profile', {
    method: 'PUT',
    body: JSON.stringify(data)
  })
};

// ============ PRODUITS ============

export const sellerProductsAPI = {
  getAll: (status = null, category = null, limit = 50, skip = 0) => {
    let url = `/api/compte/products?limit=${limit}&skip=${skip}`;
    if (status) url += `&status=${status}`;
    if (category) url += `&category=${category}`;
    return apiFetch(url);
  },
  
  getById: (productId) => apiFetch(`/api/compte/products/${productId}`),
  
  create: (data) => apiFetch('/api/compte/products', {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  
  update: (productId, data) => apiFetch(`/api/compte/products/${productId}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  
  delete: (productId) => apiFetch(`/api/compte/products/${productId}`, {
    method: 'DELETE'
  }),
  
  updateStock: (productId, stock) => apiFetch(`/api/compte/products/${productId}`, {
    method: 'PUT',
    body: JSON.stringify({ stock })
  }),
  
  toggleStatus: (productId, status) => apiFetch(`/api/compte/products/${productId}`, {
    method: 'PUT',
    body: JSON.stringify({ status })
  })
};

// ============ SERVICES (ARTISANS) ============

export const sellerServicesAPI = {
  getAll: (status = null, limit = 50) => {
    let url = `/api/compte/services?limit=${limit}`;
    if (status) url += `&status=${status}`;
    return apiFetch(url);
  },
  
  getById: (serviceId) => apiFetch(`/api/compte/services/${serviceId}`),
  
  create: (data) => apiFetch('/api/compte/services', {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  
  update: (serviceId, data) => apiFetch(`/api/compte/services/${serviceId}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  
  delete: (serviceId) => apiFetch(`/api/compte/services/${serviceId}`, {
    method: 'DELETE'
  })
};

// ============ COMMANDES REÇUES ============

export const sellerOrdersAPI = {
  getAll: (status = null, limit = 50, skip = 0) => {
    let url = `/api/compte/orders?limit=${limit}&skip=${skip}`;
    if (status) url += `&status=${status}`;
    return apiFetch(url);
  },
  
  getById: (orderId) => apiFetch(`/api/compte/orders/${orderId}`),
  
  update: (orderId, data) => apiFetch(`/api/compte/orders/${orderId}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  
  confirm: (orderId) => apiFetch(`/api/compte/orders/${orderId}`, {
    method: 'PUT',
    body: JSON.stringify({ status: 'confirmed' })
  }),
  
  ship: (orderId, trackingNumber, carrier) => apiFetch(`/api/compte/orders/${orderId}`, {
    method: 'PUT',
    body: JSON.stringify({ status: 'shipped', tracking_number: trackingNumber, carrier })
  }),
  
  markDelivered: (orderId) => apiFetch(`/api/compte/orders/${orderId}`, {
    method: 'PUT',
    body: JSON.stringify({ status: 'delivered' })
  }),
  
  cancel: (orderId, note) => apiFetch(`/api/compte/orders/${orderId}`, {
    method: 'PUT',
    body: JSON.stringify({ status: 'cancelled', seller_note: note })
  })
};

// ============ STATISTIQUES ============

export const sellerStatsAPI = {
  get: (period = 'month') => apiFetch(`/api/compte/stats?period=${period}`),
  
  getDashboard: () => apiFetch('/api/compte/stats?period=month')
};

// ============ MESSAGERIE ============

export const sellerMessagesAPI = {
  getConversations: (limit = 20) => apiFetch(`/api/compte/conversations?limit=${limit}`),
  
  getMessages: (conversationId, limit = 50) => 
    apiFetch(`/api/compte/conversations/${conversationId}/messages?limit=${limit}`),
  
  sendMessage: (conversationId, content, attachments = []) => 
    apiFetch(`/api/compte/conversations/${conversationId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ content, attachments })
    }),
  
  markAsRead: (conversationId) => 
    apiFetch(`/api/compte/conversations/${conversationId}/read`, {
      method: 'POST'
    })
};

// ============ TICKETS SAV ============

export const sellerTicketsAPI = {
  getAll: (status = null, limit = 20) => {
    let url = `/api/compte/tickets?limit=${limit}`;
    if (status) url += `&status=${status}`;
    return apiFetch(url);
  },
  
  getById: (ticketId) => apiFetch(`/api/compte/tickets/${ticketId}`),
  
  create: (data) => apiFetch('/api/compte/tickets', {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  
  reply: (ticketId, content, attachments = []) => 
    apiFetch(`/api/compte/tickets/${ticketId}/reply`, {
      method: 'POST',
      body: JSON.stringify({ content, attachments })
    }),
  
  close: (ticketId) => apiFetch(`/api/compte/tickets/${ticketId}`, {
    method: 'PUT',
    body: JSON.stringify({ status: 'closed' })
  })
};

// ============ DEVIS (CRM) ============

export const devisAPI = {
  getAll: (status = null, limit = 50) => {
    let url = `/api/compte/devis?limit=${limit}`;
    if (status) url += `&status=${status}`;
    return apiFetch(url);
  },
  
  getById: (devisId) => apiFetch(`/api/compte/devis/${devisId}`),
  
  create: (data) => apiFetch('/api/compte/devis', {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  
  update: (devisId, data) => apiFetch(`/api/compte/devis/${devisId}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  
  delete: (devisId) => apiFetch(`/api/compte/devis/${devisId}`, {
    method: 'DELETE'
  }),
  
  send: (devisId) => apiFetch(`/api/compte/devis/${devisId}/send`, {
    method: 'POST'
  }),
  
  duplicate: (devisId) => apiFetch(`/api/compte/devis/${devisId}/duplicate`, {
    method: 'POST'
  }),
  
  convertToInvoice: (devisId) => apiFetch(`/api/compte/devis/${devisId}/convert-invoice`, {
    method: 'POST'
  }),
  
  downloadPDF: (devisId) => `${API_URL}/api/compte/devis/${devisId}/pdf`,
  
  // Générer un lien portail client
  generatePortalLink: (clientId, sellerId) => apiFetch('/api/portal/generate-link', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: `client_id=${clientId}&seller_id=${sellerId}`
  })
};

// ============ FACTURES (CRM) ============

export const facturesAPI = {
  getAll: (status = null, limit = 50) => {
    let url = `/api/compte/factures?limit=${limit}`;
    if (status) url += `&status=${status}`;
    return apiFetch(url);
  },
  
  getById: (factureId) => apiFetch(`/api/compte/factures/${factureId}`),
  
  create: (data) => apiFetch('/api/compte/factures', {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  
  markPaid: (factureId) => apiFetch(`/api/compte/factures/${factureId}/mark-paid`, {
    method: 'POST'
  }),
  
  downloadPDF: (factureId) => `${API_URL}/api/compte/factures/${factureId}/pdf`
};

// ============ CLIENTS (CRM) ============

export const clientsAPI = {
  getAll: (limit = 100) => apiFetch(`/api/compte/clients?limit=${limit}`),
  
  getById: (clientId) => apiFetch(`/api/compte/clients/${clientId}`),
  
  create: (data) => apiFetch('/api/compte/clients', {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  
  update: (clientId, data) => apiFetch(`/api/compte/clients/${clientId}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  
  delete: (clientId) => apiFetch(`/api/compte/clients/${clientId}`, {
    method: 'DELETE'
  }),
  
  search: (query) => apiFetch(`/api/compte/clients/search?q=${encodeURIComponent(query)}`)
};

// ============ VENTES FLASH ============

export const sellerFlashSalesAPI = {
  getAll: () => apiFetch('/api/compte/flash-sales'),
  
  create: (data) => apiFetch('/api/compte/flash-sales', {
    method: 'POST',
    body: JSON.stringify(data)
  }),
  
  update: (flashSaleId, data) => apiFetch(`/api/compte/flash-sales/${flashSaleId}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  }),
  
  delete: (flashSaleId) => apiFetch(`/api/compte/flash-sales/${flashSaleId}`, {
    method: 'DELETE'
  })
};

// Export par défaut
export default {
  profile: sellerProfileAPI,
  products: sellerProductsAPI,
  services: sellerServicesAPI,
  orders: sellerOrdersAPI,
  stats: sellerStatsAPI,
  messages: sellerMessagesAPI,
  tickets: sellerTicketsAPI,
  devis: devisAPI,
  factures: facturesAPI,
  clients: clientsAPI,
  flashSales: sellerFlashSalesAPI
};
